import { Save, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import EmptyState from "../../components/EmptyState.jsx";
import { Field } from "../../components/FormControls.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { userUpdateRoutine } from "../../firebase/firestore.js";
import { routineEditFromRoutine, routineEditPatch } from "../../utils/routineEdit.js";
import { timeRangeLabel } from "../../utils/time.js";

function teachersForSubject(teachers = [], subjectId) {
  if (!subjectId) return teachers;
  return teachers.filter((teacher) => {
    const subjectIds = Array.isArray(teacher.subjectIds) ? teacher.subjectIds : [];
    return subjectIds.includes(subjectId) || teacher.subjectId === subjectId;
  });
}

function RoutineEditCard({ routine, masters, onSave, savingId }) {
  const [form, setForm] = useState(routineEditFromRoutine(routine));
  const teachers = teachersForSubject(masters.teachers, form.subjectId);
  const saving = savingId === (routine.id || routine.routineId);

  function chooseSubject(subjectId) {
    const subject = masters.subjects.find((item) => item.id === subjectId || item.subjectId === subjectId);
    setForm((value) => ({
      ...value,
      subjectId,
      subjectName: subject?.name || "",
      teacherId: "",
      teacherName: ""
    }));
  }

  function chooseTeacher(teacherId) {
    const teacher = masters.teachers.find((item) => item.id === teacherId || item.teacherId === teacherId);
    setForm((value) => ({
      ...value,
      teacherId,
      teacherName: teacher?.name || ""
    }));
  }

  return (
    <article className="card routine-edit-card">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{routine.className} - {routine.sectionName}</p>
          <h3>{routine.subjectName || "Subject"} · Room {routine.roomNumber || "N/A"}</h3>
        </div>
        <SlidersHorizontal size={20} />
      </div>
      <p className="muted-copy">{timeRangeLabel(routine.scheduledIn, routine.scheduledOut)} · {routine.teacherName || "No teacher"}</p>

      <div className="form-grid two">
        <Field label="Room number">
          <input value={form.roomNumber || ""} onChange={(event) => setForm((value) => ({ ...value, roomNumber: event.target.value }))} placeholder="Room number" />
        </Field>
        <Field label="Subject">
          <select value={form.subjectId || ""} onChange={(event) => chooseSubject(event.target.value)} required>
            <option value="">Select subject</option>
            {masters.subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>{subject.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Teacher">
          <select value={form.teacherId || ""} onChange={(event) => chooseTeacher(event.target.value)} required>
            <option value="">Select teacher</option>
            {teachers.map((teacher) => (
              <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Routine IN">
          <input type="time" value={form.scheduledIn || ""} onChange={(event) => setForm((value) => ({ ...value, scheduledIn: event.target.value }))} required />
        </Field>
        <Field label="Routine OUT">
          <input type="time" value={form.scheduledOut || ""} onChange={(event) => setForm((value) => ({ ...value, scheduledOut: event.target.value }))} required />
        </Field>
      </div>

      <div className="form-actions">
        <button className="btn btn-primary" type="button" onClick={() => onSave(routine, form)} disabled={saving}>
          {saving ? <span className="spinner" /> : <Save size={17} />}
          Save Routine
        </button>
      </div>
    </article>
  );
}

export default function UserRoutineSettings() {
  const { routines, routineMasters, sectionFilter } = useOutletContext();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [savingId, setSavingId] = useState("");

  const editableRoutines = useMemo(() => {
    return [...routines].sort((a, b) => {
      const classCompare = String(a.className || "").localeCompare(String(b.className || ""), undefined, { numeric: true });
      if (classCompare !== 0) return classCompare;
      const sectionCompare = String(a.sectionName || "").localeCompare(String(b.sectionName || ""), undefined, { numeric: true });
      if (sectionCompare !== 0) return sectionCompare;
      return String(a.scheduledIn || "").localeCompare(String(b.scheduledIn || ""));
    });
  }, [routines]);

  async function saveRoutineEdit(routine, form) {
    const patch = routineEditPatch(routine, form);
    const id = routine.id || routine.routineId;
    if (!Object.keys(patch).length) {
      showToast("No routine change found", "warning");
      return;
    }
    setSavingId(id);
    try {
      await userUpdateRoutine(routine, patch, user);
      showToast("Routine updated permanently");
    } catch (error) {
      showToast(error.message || "Unable to update routine", "error");
    } finally {
      setSavingId("");
    }
  }

  return (
    <section className="user-settings-page">
      <div className="page-hero compact">
        <p>{sectionFilter.length ? "Selected sections only" : "All visible routines"}</p>
        <h2>Routine Settings</h2>
      </div>
      {editableRoutines.length ? (
        <div className="routine-edit-list">
          {editableRoutines.map((routine) => (
            <RoutineEditCard
              key={routine.id || routine.routineId}
              routine={routine}
              masters={routineMasters || { teachers: [], subjects: [] }}
              onSave={saveRoutineEdit}
              savingId={savingId}
            />
          ))}
        </div>
      ) : (
        <EmptyState icon="🛠️" title="No editable routine" text="Choose sections first or wait for today’s routine to load." />
      )}
    </section>
  );
}
